/**
 * Pure stat computation — port of `selectMech`/`selectPilot` from
 * `reference/compcon` (`Mech.ts`/`Pilot.ts`) and the cli's selector layer,
 * rewritten as a pure `compute(raw, compendium) → { pilot, mech }`.
 *
 * This is the *computed* half of the raw-vs-computed split: a pure derivation over
 * raw `PilotData` + the local `Compendium`. Nothing here is stored, synced, or
 * undone — the wire carries the raw delta and every client runs this to land on
 * identical numbers.
 *
 * All MVP stats from the spec Appendix are implemented:
 *   grit, HP, evasion, e-defense, speed, heat cap, repair cap, save target,
 *   sensor range, tech attack, SP (budget + used), size, limited bonus. Also
 * computed: `ComputedPilot.combat`, the on-foot pilot combat-stat block (Core
 * p. 28/48) — see {@link computePilotStats}.
 *
 * Bonus *sources* wired: frame traits (`frame.bonuses`) + equipped weapon, weapon-mod,
 * and system bonuses (all mech-scoped, via `collectMechBonuses`), plus pilot
 * **talent**-rank and **core-bonus** bonuses (both pilot-scoped, via
 * `collectPilotBonuses`), plus equipped **personal-gear** (`pilot_gear`) bonuses
 * (pilot-scoped, via `collectPilotGearBonuses` — feeds only `computePilotStats`,
 * since `pilot_*` bonus ids are a disjoint namespace from mech stat ids). HASE and
 * grit are folded into the base via the formulas (not bonus objects). The engine
 * is ported complete — every remaining bonus *id* is collected; one only
 * *surfaces* once a matching `ComputedMechStats`/`ComputedPilotStats` read exists
 * (e.g. `hp` does, so a `+hp` core bonus visibly lands; `ai_cap` and the
 * core-bonus choice ids — `immunity`/`boost`/… — are knowingly deferred, sparse).
 *
 * **`range` and `add_mount` are deliberately NOT handled here**, because neither was
 * ever a `ComputedMechStats` field: `range` is a per-weapon-band adjustment, resolved
 * off the pilot's core bonuses and folded into every ranged weapon's Range-type band(s)
 * at spawn-freeze (`spawn.ts`'s `resolvePilotRangeBonus`/`freezeWeapons`); `add_mount`
 * is a mount-array composition, read directly by `composedMounts`
 * (`@lancer-vtt/domain`), which composes the granted bonus mount (Improved Armament's
 * Flex, Integrated Weapon's Aux) or swaps a native mount's type in place (Mount
 * Retrofitting). Neither goes through this module's `apply(id, …)` machinery.
 */

import { basePilotStats } from "@lancer-vtt/compendium";
import type { Compendium } from "@lancer-vtt/compendium";
import type { MechData, PilotData } from "@lancer-vtt/domain";
import { computeSpMax, computeSpUsed, REPAIR_COST_STRUCTURE } from "@lancer-vtt/domain";
import { makeBonusApplier, type Bonus, type ModifierVars } from "./bonus";

/**
 * A frame trait's own `bonuses` id — halves the Repair cost of restoring 1 Structure
 * (Core p. 82's 2-per-point default → 1; the Everest's "Replaceable parts":
 * `reference/lancer-data/lib/frames.json`'s
 * `mf_standard_pattern_i_everest.traits[1].bonuses[0].id === "cheap_struct"`). Matches
 * COMP/CON's own `BonusId.CHEAP_STRUCT` (`reference/compcon/.../bonus_dictionary.ts`) —
 * a **presence** flag, not an additive/replace stat (COMP/CON's own dictionary never
 * actually consumes it either; this project is the first real reader). Deliberately NOT
 * routed through `makeBonusApplier`'s additive/replace/overwrite math (`apply("cheap_struct",
 * REPAIR_COST_STRUCTURE)` would nonsensically ADD the bonus's `val` onto the cost) — see
 * {@link computeMech}'s `structureRepairCost` read.
 */
const CHEAP_STRUCT_BONUS_ID = "cheap_struct";

/** Pilot grit: `ceil(level / 2)`. */
export function getGrit(level: number): number {
  return Math.ceil(level / 2);
}

/** Computed mech stats — every MVP stat from the Appendix, post-bonus. */
export type ComputedMechStats = {
  size: number;
  hp: number;
  evasion: number;
  edef: number;
  speed: number;
  heatcap: number;
  repcap: number;
  save: number;
  sensorRange: number;
  techAttack: number;
  /** SP budget available. */
  spMax: number;
  /** SP consumed by equipped weapons + systems. */
  spUsed: number;
  limitedBonus: number;
  /**
   * Repairs cost to restore 1 Structure (Core p. 82) — `REPAIR_COST_STRUCTURE` (2) by
   * default, `1` when the frame carries a `cheap_struct` bonus (the Everest's "Replaceable
   * parts" trait). See {@link CHEAP_STRUCT_BONUS_ID}.
   */
  structureRepairCost: number;
}

/** A computed mech: its id/name + frame id + resolved stats. */
export type ComputedMech = {
  id: string;
  name: string;
  frameId: string;
  stats: ComputedMechStats;
}

/**
 * Computed on-foot pilot combat stats (Core p. 28/48) — a pilot's personal stats when
 * fighting out of the mech, post-bonus. **Pinned seam**: the dismounted-pilot spawn reads
 * this exactly the way `spawnMech` reads `ComputedMech.stats`. Extend here; don't fork a
 * parallel compute path.
 */
export type ComputedPilotStats = {
  /** Constant 0.5 — no `pilot_size` bonus id exists in COMP/CON. */
  size: number;
  /** `6 + grit`, post `pilot_hp`. Grit applies to HP only, on foot. */
  hp: number;
  /** `0`, post `pilot_armor` (no `base_pilot_armor` rules.json key). */
  armor: number;
  /** `10`, post `pilot_evasion` (armor sets this via `replace`). */
  evasion: number;
  /** `10`, post `pilot_edef` (replace-mode). */
  edef: number;
  /** `4`, post `pilot_speed` (replace-mode). */
  speed: number;
  /** Constant 5 — no `pilot_sensor` bonus id exists in COMP/CON. */
  sensorRange: number;
  /**
   * `10 + grit` — RAW p.74: "unmounted characters make all attacks and saves by
   * adding GRIT". Parallels the
   * mech's `s.save + grit` fold (see `computeMech` below). No `pilot_save`
   * bonus id exists in COMP/CON, so this is a plain arithmetic fold, not an
   * `apply(...)` call.
   */
  save: number;
  /** Equals `grit` (not grit-added-to-something); Fight is the umbrella's. */
  attackBonus: number;
  /** Equals `grit`; on-foot tech consumption is the umbrella's. */
  techAttack: number;
}

/** A computed pilot: identity + grit + its computed mechs + on-foot combat stats. */
export type ComputedPilot = {
  id: string;
  callsign: string;
  name: string;
  level: number;
  grit: number;
  mechs: ComputedMech[];
  combat: ComputedPilotStats;
}

/**
 * Pull the bonus objects the engine should see for a mech: the frame's traits
 * plus every equipped weapon's and system's bonuses, from the active loadout.
 * Items carry `bonuses` via the compendium's `.passthrough()`, so they arrive
 * untyped — we read them structurally and trust the shape.
 */
function collectMechBonuses(
  mech: MechData,
  compendium: Compendium,
): Bonus[] {
  const bonuses: Bonus[] = [];

  if (mech.frameId) {
    const frame = compendium.getById("frame", mech.frameId) as
      | { bonuses?: Bonus[]; traits?: { bonuses?: Bonus[] }[] }
      | undefined;
    if (frame?.bonuses) bonuses.push(...frame.bonuses);
    // A frame TRAIT's own `bonuses` — distinct from the frame's top-level `bonuses`, which is
    // null for every core frame, so without this walk the Everest's "Replaceable parts"
    // `cheap_struct` bonus never reaches the applier. Traits carry no native id, so no
    // per-trait provenance is needed here — just fold every trait's bonuses in, mirroring
    // `frame.bonuses` itself.
    if (Array.isArray(frame?.traits)) {
      for (const trait of frame.traits) if (trait?.bonuses) bonuses.push(...trait.bonuses);
    }
  }

  const loadout = mech.loadouts[mech.activeLoadoutIndex];
  if (loadout) {
    for (const mount of loadout.mounts) {
      for (const slot of mount.slots) {
        if (!slot.weaponId) continue;
        const weapon = compendium.getById("weapon", slot.weaponId) as
          | { bonuses?: Bonus[] }
          | undefined;
        if (weapon?.bonuses) bonuses.push(...weapon.bonuses);

        // A mod rides its host weapon (COMP/CON `MechWeapon._mod`); read its
        // bonuses the same structural way. Moot for core data (0/10 mods carry
        // any), but the seam is here so a community LCP mod's bonuses flow.
        if (slot.modId) {
          const mod = compendium.getById("mod", slot.modId) as
            | { bonuses?: Bonus[] }
            | undefined;
          if (mod?.bonuses) bonuses.push(...mod.bonuses);
        }
      }
    }

    // Equipped systems are loadout-scoped (COMP/CON `MechLoadout._systems`), so
    // they're mech-scoped bonuses — collected here, not in `collectPilotBonuses`.
    // Items carry `bonuses` via the compendium's `.passthrough()`, untyped, read
    // structurally like `frame.bonuses`. Core systems target `ai_cap`/`hp`/`range`;
    // only `hp` is a computed stat today, so it's the one that visibly flows. The
    // loop sources all ids — `ai_cap`/`range` are collected but won't surface until
    // a matching `ComputedMechStats` read is added (knowingly deferred — sparse,
    // 3/149 systems carry any bonus; adding empty-base stats over-builds the engine).
    for (const systemId of loadout.systems ?? []) {
      const system = compendium.getById("system", systemId) as
        | { bonuses?: Bonus[] }
        | undefined;
      if (system?.bonuses) bonuses.push(...system.bonuses);
    }
  }

  return bonuses;
}

/**
 * Pull the bonus objects a pilot contributes pilot-wide: **talent** ranks and
 * **core bonuses**. Both are pilot-scoped (they apply to every mech the pilot
 * owns), so this lives apart from {@link collectMechBonuses} — it sees
 * `pilot.talents`/`pilot.coreBonuses`, not a single mech.
 *
 * Talents: for each owned talent at chosen rank N, the unlocked ranks are
 * CUMULATIVE (1..N, COMP/CON's `UnlockedRanks`), so we sum `ranks[i].bonuses`
 * for every `i` in `0..N-1`, not just the top rank. A talent rank carries
 * `bonuses` via the compendium's `.passthrough()` (`TalentSchema.ranks`), so it
 * arrives untyped — read it structurally like `frame.bonuses`. NOTE: no core
 * talent carries a mech-stat bonus id (only weapon/damage/deployable ids exist on
 * the 4 talents that have any); the seam is here for community LCPs that do.
 *
 * Core bonuses: `pilot.coreBonuses` is a flat list of taken `core_bonus` ids (the
 * budget + per-manufacturer prereq are enforced upstream at `set_core_bonus` time,
 * not here — compute is a pure derivation over whatever the raw pilot holds, like
 * talent ranks). 19/30 core bonuses carry a `bonuses` array, also via the
 * compendium's `.passthrough()`, read structurally. Unlike talents, real core
 * bonuses move real mech stats (`hp`/`heatcap`/`evasion`/`edef`/`save`/…), so the
 * applier surfaces them against the matching `ComputedMechStats` read.
 *
 * Collected-but-inert (the same `ai_cap`/`range` situation {@link collectMechBonuses}
 * has): the non-flat-stat / choice ids these bonuses also carry — `mount_accuracy`,
 * `no_mods`, `immunity`, `boost`, `overcharge`, `limited_bonus`, plus the sparse
 * `hull`/`agility`/`armor`/`knockback`/`ai_cap` ids no `ComputedMechStats` field reads —
 * don't reduce to a single additive stat. They're pushed into the bonus list all the same,
 * but only an id with a matching `apply(id, …)` call in {@link computeMech} visibly lands.
 *
 * **`range` and `add_mount` are read elsewhere, never from this list**: a core bonus's
 * `range` row (e.g. `cb_neurolink_targeting`) is read by `spawn.ts`'s
 * `resolvePilotRangeBonus` straight off `pilot.coreBonuses` at spawn-freeze, and its
 * `add_mount` row (e.g. `cb_improved_armament`/`cb_integrated_weapon`) by
 * `@lancer-vtt/domain`'s `composedMounts`, likewise straight off `pilot.coreBonuses` —
 * since neither a per-weapon-band adjustment nor a mount-array composition was ever a
 * `ComputedMechStats` field. The copies landing HERE are inert, kept only because every
 * core-bonus row flows through the generic collector.
 */
function collectPilotBonuses(
  pilot: PilotData,
  compendium: Compendium,
): Bonus[] {
  const bonuses: Bonus[] = [];
  for (const [talentId, rank] of Object.entries(pilot.talents)) {
    const talent = compendium.getById("talent", talentId);
    if (!talent) continue;
    for (let i = 0; i < rank && i < talent.ranks.length; i++) {
      const r = talent.ranks[i] as { bonuses?: Bonus[] };
      if (r.bonuses) bonuses.push(...r.bonuses);
    }
  }
  for (const bonusId of pilot.coreBonuses) {
    const cb = compendium.getById("core_bonus", bonusId) as
      | { bonuses?: Bonus[] }
      | undefined;
    if (cb?.bonuses) bonuses.push(...cb.bonuses);
  }
  return bonuses;
}

/**
 * Pull the bonus objects a pilot's equipped personal gear contributes —
 * `pilot.gear` resolved against `pilot_gear` compendium items, reading each
 * item's `bonuses` structurally (the compendium's `.passthrough()` pattern, same
 * as {@link collectMechBonuses}). Only the 5 armor items carry any `pilot_*`
 * bonus in core data (weapons/gear ship none); an unresolvable id is skipped,
 * mirroring `pilotGearCounts`' derive-then-skip discipline.
 */
function collectPilotGearBonuses(
  pilot: PilotData,
  compendium: Compendium,
): Bonus[] {
  const bonuses: Bonus[] = [];
  for (const id of pilot.gear) {
    const item = compendium.getById("pilot_gear", id) as
      | { bonuses?: Bonus[] }
      | undefined;
    if (item?.bonuses) bonuses.push(...item.bonuses);
  }
  return bonuses;
}

/**
 * Compute a pilot's on-foot combat stats (Core p. 28/48) — a pure derivation over
 * `PilotData` + the compendium. Bases come from {@link basePilotStats} (never
 * hardcoded) plus two constants
 * with no `rules.json`/bonus-id backing (`size`, and `armor`'s base-0). The
 * applier feeds BOTH gear bonuses and the existing pilot-scoped
 * `collectPilotBonuses` output (talent ranks + core bonuses) — id-disjoint from
 * mech bonuses (`pilot_*` vs. mech ids), so feeding both is safe and lets
 * community LCP content put a `pilot_*` bonus on a talent/core-bonus for free.
 */
function computePilotStats(
  pilot: PilotData,
  compendium: Compendium,
): ComputedPilotStats {
  const grit = getGrit(pilot.level);
  const { hull, agi, sys, eng } = pilot.mechSkills;
  const vars: ModifierVars = { grit, ll: pilot.level, hull, agi, sys, eng };

  const bonuses = [
    ...collectPilotGearBonuses(pilot, compendium),
    ...collectPilotBonuses(pilot, compendium),
  ];
  const apply = makeBonusApplier(bonuses, vars);

  const base = basePilotStats();

  return {
    size: 0.5,
    hp: apply("pilot_hp", base.hp + grit),
    armor: apply("pilot_armor", 0),
    evasion: apply("pilot_evasion", base.evasion),
    edef: apply("pilot_edef", base.edef),
    speed: apply("pilot_speed", base.speed),
    sensorRange: base.sensorRange,
    save: base.save + grit,
    attackBonus: grit,
    techAttack: grit,
  };
}

/**
 * Compute a single mech's stats. Returns `null` if the mech has no frame yet or
 * the frame id doesn't resolve in the compendium (a half-built mech has no
 * stats to show).
 */
function computeMech(
  mech: MechData,
  pilot: PilotData,
  compendium: Compendium,
): ComputedMech | null {
  if (!mech.frameId) return null;
  const frame = compendium.getById("frame", mech.frameId);
  if (!frame) return null;

  const s = frame.stats;
  const { hull, agi, sys, eng } = pilot.mechSkills;
  const grit = getGrit(pilot.level);

  const vars: ModifierVars = { grit, ll: pilot.level, hull, agi, sys, eng };
  // Mech-scoped (frame + equipped items) and pilot-scoped (talent) bonuses both
  // feed the same applier; talent bonuses are pilot-wide so they're collected from
  // `pilot.talents` here, not folded into the mech-only `collectMechBonuses`.
  const bonuses = [...collectMechBonuses(mech, compendium), ...collectPilotBonuses(pilot, compendium)];
  const apply = makeBonusApplier(bonuses, vars);

  // Each stat: base (frame) + HASE/grit contribution, then bonuses on top.
  // Bonus ids match COMP/CON's BonusId strings.
  let size = apply("size", s.size);
  if (size < 0.5) size = 0.5;

  const stats: ComputedMechStats = {
    size,
    hp: apply("hp", s.hp + grit + hull * 2),
    evasion: apply("evasion", s.evasion + agi),
    edef: apply("edef", s.edef + sys),
    speed: apply("speed", s.speed + Math.floor(agi / 2)),
    heatcap: apply("heatcap", s.heatcap + eng),
    repcap: apply("repcap", s.repcap + Math.floor(hull / 2)),
    save: apply("save", s.save + grit),
    sensorRange: apply("sensor", s.sensor_range),
    techAttack: apply("tech_attack", s.tech_attack + sys),
    // SP budget + used come from the shared `@lancer-vtt/domain` helpers (the single
    // source the reducer wall also imports, so the gate and this display can't drift).
    // `computeSpMax` returns the canonical base (`frame.sp + grit + floor(sys/2)`);
    // the `apply("sp", …)` wrapper layers any `BonusId.SP` bonuses on top (display only).
    spMax: apply("sp", computeSpMax(mech, pilot, compendium)),
    spUsed: computeSpUsed(mech, pilot, compendium),
    limitedBonus: apply("limited_bonus", Math.floor(eng / 2)),
    // Presence check, not `apply(...)` — see `CHEAP_STRUCT_BONUS_ID`'s doc.
    structureRepairCost: bonuses.some((b) => b.id === CHEAP_STRUCT_BONUS_ID) ? 1 : REPAIR_COST_STRUCTURE,
  };

  return { id: mech.id, name: mech.name, frameId: mech.frameId, stats };
}

/**
 * The MVP entry point: derive computed views for a pilot and all its mechs from
 * raw `PilotData` + the local `Compendium`. Mechs that can't resolve a frame are
 * omitted from `mech` (they have nothing to compute yet).
 */
export function compute(
  pilot: PilotData,
  compendium: Compendium,
): { pilot: ComputedPilot; mechs: ComputedMech[] } {
  const grit = getGrit(pilot.level);

  const mechs = pilot.mechs
    .map((m) => computeMech(m, pilot, compendium))
    .filter((m): m is ComputedMech => m !== null);

  const computedPilot: ComputedPilot = {
    id: pilot.id,
    callsign: pilot.callsign,
    name: pilot.name,
    level: pilot.level,
    grit,
    mechs,
    combat: computePilotStats(pilot, compendium),
  };

  return { pilot: computedPilot, mechs };
}
